import { quranStructure } from './constants';
import {
  globalAyahNumber, surahRange, juzRange,
  TOTAL_QURAN_VERSES, TOTAL_SURAHS, TOTAL_JUZ
} from './quranIndex';
import { dateKey, ayahsReadBetween, ayahsReadUpTo, ayahsReadOn, cycleAyahs } from './readingLog';

export const GOAL_STORAGE_KEY = 'quranReaderGoal';

export const PERIODS = ['day', 'week', 'month', 'year'];
export const UNITS = ['verses', 'surahs', 'juz'];

export const PERIOD_LABELS = { day: 'day', week: 'week', month: 'month', year: 'year' };
export const UNIT_LABELS = { verses: 'verses', surahs: 'surahs', juz: 'juz' };

export const unitTotal = (unit) =>
  unit === 'verses' ? TOTAL_QURAN_VERSES : unit === 'surahs' ? TOTAL_SURAHS : TOTAL_JUZ;

// ---------------------------------------------------------------- periods

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date, days) => {
  const copy = startOfDay(date);
  copy.setDate(copy.getDate() + days);
  return copy;
};

// Weeks start on Monday.
const startOfWeek = (date) => {
  const day = startOfDay(date);
  const weekday = (day.getDay() + 6) % 7; // Monday = 0
  return addDays(day, -weekday);
};

// The window a date falls in, as inclusive YYYY-MM-DD bounds.
export const periodBounds = (date, period) => {
  const day = startOfDay(date);
  switch (period) {
    case 'week': {
      const start = startOfWeek(day);
      return { startKey: dateKey(start), endKey: dateKey(addDays(start, 6)) };
    }
    case 'month': {
      const start = new Date(day.getFullYear(), day.getMonth(), 1);
      const end = new Date(day.getFullYear(), day.getMonth() + 1, 0);
      return { startKey: dateKey(start), endKey: dateKey(end) };
    }
    case 'year':
      return {
        startKey: dateKey(new Date(day.getFullYear(), 0, 1)),
        endKey: dateKey(new Date(day.getFullYear(), 11, 31))
      };
    case 'day':
    default:
      return { startKey: dateKey(day), endKey: dateKey(day) };
  }
};

// A period is identified by the date it starts on, which sidesteps the corner
// cases of ISO week numbering at the turn of a year.
export const periodKey = (date, period) => periodBounds(date, period).startKey;

// The same window, shifted back by `count` periods.
export const shiftPeriod = (date, period, count) => {
  const day = startOfDay(date);
  switch (period) {
    case 'week': return addDays(day, -7 * count);
    case 'month': return new Date(day.getFullYear(), day.getMonth() - count, 1);
    case 'year': return new Date(day.getFullYear() - count, day.getMonth(), 1);
    case 'day':
    default: return addDays(day, -count);
  }
};

export const daysInPeriod = (date, period) => {
  const { startKey, endKey } = periodBounds(date, period);
  const start = new Date(`${startKey}T00:00:00`);
  const end = new Date(`${endKey}T00:00:00`);
  return Math.round((end - start) / 86400000) + 1;
};

// ---------------------------------------------------------------- counting

const isRangeComplete = (readSet, range) => {
  for (let ayah = range.first; ayah <= range.last; ayah += 1) {
    if (!readSet.has(ayah)) return false;
  }
  return true;
};

const completedRanges = (readSet, ranges) =>
  ranges.reduce((count, range) => (isRangeComplete(readSet, range) ? count + 1 : count), 0);

const surahRanges = quranStructure.map((surah) => surahRange(surah.number));
const juzRanges = Array.from({ length: TOTAL_JUZ }, (unused, index) => juzRange(index + 1));

// How much of `unit` was completed inside the window.
//
// Verses count on the day they were read. A surah or a juz counts for the
// window in which its *last* missing verse was read: finishing Al-Baqarah today
// credits today, even though it was begun weeks ago.
export const countUnits = (log, { startKey, endKey }, unit) => {
  if (unit === 'verses') return ayahsReadBetween(log, startKey, endKey).size;

  const ranges = unit === 'juz' ? juzRanges : surahRanges;
  const atEnd = ayahsReadUpTo(log, endKey);

  // The day before the window opens
  const dayBefore = new Date(`${startKey}T00:00:00`);
  dayBefore.setDate(dayBefore.getDate() - 1);
  const atStart = ayahsReadUpTo(log, dateKey(dayBefore));

  return completedRanges(atEnd, ranges) - completedRanges(atStart, ranges);
};

// ---------------------------------------------------------------- goals

// Reminders are delivered by an hourly job, so the times are whole hours.
// At most four of them: past that it stops being a reminder and becomes noise.
export const MAX_REMINDER_HOURS = 4;

export const sanitiseReminderHours = (hours) => {
  if (!Array.isArray(hours)) return [];
  const clean = hours
    .map((hour) => Number(hour))
    .filter((hour) => Number.isInteger(hour) && hour >= 0 && hour <= 23);
  return [...new Set(clean)].sort((a, b) => a - b).slice(0, MAX_REMINDER_HOURS);
};

export const makeGoal = ({ period, unit, amount, reminderHours = [20], reminderEnabled = true }) => ({
  period,
  unit,
  amount: Math.max(1, Math.round(amount)),
  reminderHours: sanitiseReminderHours(reminderHours),
  reminderEnabled,
  createdAt: new Date().toISOString()
});

export const isValidGoal = (goal) =>
  Boolean(goal) &&
  PERIODS.includes(goal.period) &&
  UNITS.includes(goal.unit) &&
  Number.isFinite(goal.amount) &&
  goal.amount >= 1;

// Goals saved before reminders moved to whole hours carry a 'HH:MM' string.
export const migrateGoal = (goal) => {
  if (!goal || Array.isArray(goal.reminderHours)) return goal;
  const hour = parseInt(String(goal.reminderTime || '20:00').split(':')[0], 10);
  const { reminderTime, ...rest } = goal;
  return { ...rest, reminderHours: sanitiseReminderHours([hour]) };
};

export const goalProgress = (goal, log, today = new Date()) => {
  const bounds = periodBounds(today, goal.period);
  const done = countUnits(log, bounds, goal.unit);
  const target = goal.amount;
  return {
    done,
    target,
    remaining: Math.max(0, target - done),
    percent: target > 0 ? Math.min(100, (done / target) * 100) : 0,
    met: done >= target,
    bounds
  };
};

// Consecutive completed periods. The period in progress only counts once it is
// met, so a streak is never broken by a day that still has hours left in it.
export const goalStreak = (goal, log, today = new Date(), maxLookBack = 400) => {
  let streak = 0;
  const current = goalProgress(goal, log, today);
  let offset = current.met ? 0 : 1;

  for (let step = 0; step < maxLookBack; step += 1) {
    const date = shiftPeriod(today, goal.period, offset + step);
    const bounds = periodBounds(date, goal.period);
    if (countUnits(log, bounds, goal.unit) >= goal.amount) streak += 1;
    else break;
  }
  return streak;
};

// Consecutive days with at least one verse read, whatever the goal.
//
// A day still in progress does not break the run: the count only falls to zero
// once a whole day has passed with nothing read. Resetting at every midnight
// would show zero each morning, which is the opposite of encouraging.
export const readingStreak = (log, today = new Date(), maxLookBack = 3650) => {
  let cursor = startOfDay(today);

  // Nothing read yet today: the run stands on yesterday until the day is out
  if (ayahsReadOn(log, dateKey(cursor)).length === 0) cursor = addDays(cursor, -1);

  let days = 0;
  for (let step = 0; step < maxLookBack; step += 1) {
    if (ayahsReadOn(log, dateKey(cursor)).length === 0) break;
    days += 1;
    cursor = addDays(cursor, -1);
  }
  return days;
};

export const hasReadToday = (log, today = new Date()) =>
  ayahsReadOn(log, dateKey(today)).length > 0;

// Pace so far, and when the whole Quran would be finished at that pace.
export const goalProjection = (goal, log, today = new Date()) => {
  const total = unitTotal(goal.unit);
  // Progress through the reading in progress, so it restarts after a khatm
  // rather than sitting at 100% forever.
  const inCycle = cycleAyahs(log);
  const doneOverall = goal.unit === 'verses'
    ? inCycle.size
    : completedRanges(inCycle, goal.unit === 'juz' ? juzRanges : surahRanges);

  const perDay = goal.amount / daysInPeriod(today, goal.period);
  const remaining = Math.max(0, total - doneOverall);
  const daysNeeded = perDay > 0 ? Math.ceil(remaining / perDay) : null;

  let finishDate = null;
  if (daysNeeded !== null && daysNeeded < 365 * 50) {
    finishDate = addDays(today, daysNeeded);
  }

  return {
    total,
    completed: doneOverall,
    percent: total > 0 ? (doneOverall / total) * 100 : 0,
    perDay,
    daysNeeded,
    finishDate
  };
};

// Presets, with the pace worked out rather than left to the reader.
export const GOAL_PRESETS = [
  { id: 'khatm-year', label: 'Finish the Quran in a year', period: 'day', unit: 'verses', amount: Math.ceil(TOTAL_QURAN_VERSES / 365) },
  { id: 'khatm-6m', label: 'Finish the Quran in six months', period: 'day', unit: 'verses', amount: Math.ceil(TOTAL_QURAN_VERSES / 182) },
  { id: 'juz-week', label: 'One juz a week', period: 'week', unit: 'juz', amount: 1 },
  { id: 'ramadan', label: 'One juz a day (Ramadan pace)', period: 'day', unit: 'juz', amount: 1 },
  { id: 'surah-week', label: 'One surah a week', period: 'week', unit: 'surahs', amount: 1 }
];

export { globalAyahNumber };
